/**
 * useCallTone — WebAudio call tones, phone-app style.
 *
 * Two distinct sounds, matching what a real phone does:
 *   - 'ringback'  → what the CALLER hears while waiting for the other side to
 *                   pick up: the classic dual-tone "ring-ring" (440 + 480 Hz).
 *   - 'melody'    → what the RECEIVER hears on an incoming call: a short, cheerful
 *                   marimba-like jingle that loops (a ringtone, not a beep).
 *
 * The AudioContext is created suspended under the browser autoplay policy, so we
 * resume() it (the staff member is already interacting with the app, so the
 * gesture requirement is satisfied) — otherwise the tone is silent. The whole
 * hook no-ops in environments without WebAudio (happy-dom / jsdom in tests).
 *
 * The schedulers (playRingback / playMelody) are pure given an AudioContext-like
 * object, so they can be unit-tested with a fake context. See useCallTone.test.ts.
 */
import { useEffect, useRef } from 'react';

export type CallToneKind = 'ringback' | 'melody';

/** One dual-tone burst (440 + 480 Hz) with a click-free envelope. */
function ringBurst(ctx: AudioContext, start: number, dur = 0.4): void {
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.09, start + 0.04);
  gain.gain.setValueAtTime(0.09, start + dur - 0.06);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  gain.connect(ctx.destination);
  for (const freq of [440, 480]) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, start);
    osc.connect(gain);
    osc.start(start);
    osc.stop(start + dur);
  }
}

/** Caller-side ringback: a "ring-ring" (two bursts ~0.55 s apart). */
export function playRingback(ctx: AudioContext): void {
  const t = ctx.currentTime;
  ringBurst(ctx, t);
  ringBurst(ctx, t + 0.55);
}

/** One melody note — a triangle wave with a percussive (marimba/bell) envelope. */
function pluck(ctx: AudioContext, freq: number, start: number, dur: number, peak = 0.12): void {
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  gain.connect(ctx.destination);
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(freq, start);
  osc.connect(gain);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

// A short cheerful jingle in G major — ascending arpeggio then a resolve.
// [offsetSeconds, freqHz, durationSeconds].
const MELODY: ReadonlyArray<readonly [number, number, number]> = [
  [0.0, 783.99, 0.14], // G5
  [0.16, 987.77, 0.14], // B5
  [0.32, 1174.66, 0.14], // D6
  [0.48, 1567.98, 0.2], // G6
  [0.74, 1318.51, 0.14], // E6
  [0.9, 1174.66, 0.14], // D6
  [1.06, 987.77, 0.3], // B5
];

/** Receiver-side ringtone: play one pass of the melody from now. */
export function playMelody(ctx: AudioContext): void {
  const t = ctx.currentTime;
  for (const [offset, freq, dur] of MELODY) {
    pluck(ctx, freq, t + offset, dur);
  }
}

// Loop cadences (melody pass ≈1.36 s + gap; ring-ring + gap).
export const MELODY_PERIOD_MS = 2500;
export const RINGBACK_PERIOD_MS = 3000;

function getAudioCtor(): typeof AudioContext | undefined {
  if (typeof AudioContext !== 'undefined') return AudioContext;
  const w = window as { webkitAudioContext?: typeof AudioContext };
  return typeof w.webkitAudioContext !== 'undefined' ? w.webkitAudioContext : undefined;
}

/**
 * Play a looping call tone while `active` is true. No-ops silently when WebAudio
 * is unavailable. Switching `kind` while active restarts the loop.
 */
export function useCallTone(active: boolean, kind: CallToneKind): void {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!active) return;

    const Ctor = getAudioCtor();
    if (!Ctor) return;

    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return; // creation blocked by policy
    }
    ctxRef.current = ctx;
    void ctx.resume?.();

    const tick = (): void => {
      const c = ctxRef.current;
      if (!c || c.state === 'closed') return;
      if (kind === 'ringback') playRingback(c);
      else playMelody(c);
    };

    tick(); // play immediately, don't wait one period
    const period = kind === 'ringback' ? RINGBACK_PERIOD_MS : MELODY_PERIOD_MS;
    intervalRef.current = setInterval(tick, period);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      try {
        ctxRef.current?.close();
      } catch {
        /* ignore close errors */
      }
      ctxRef.current = null;
    };
  }, [active, kind]);
}
